import { Injectable } from '@angular/core';
import { StepFourModel } from '../../../shared/models/states/loan/step-four.model';
import { GetLoanTotals } from '../../../shared/interfaces/loan-total.interface';
import { LoanTotal } from '../../../shared/models/loan-total.model';
import { BrowserStorageService, StorageKeys } from '../../services/browser-storage.service';
import { AssetsService } from '../../services/loan/assets.api.service';
import { StepTwoState } from './step-two-state';
import { forkJoin, lastValueFrom, Observable, tap } from 'rxjs';
import { isAssetEmpty } from '../../../shared/models/asset.model';

@Injectable({
    providedIn: 'root',
})
export class StepFourState implements GetLoanTotals {
    private _state?: StepFourModel;
    public readonly assetHeaders: string[] = [
        'Deposits With Central Bank',
        'Deposits With Other Institutions',
        'Marketable Securities',
        'Face Value Life Insurance',
        'Cash Value Life Insurance',
        'IRA / Pension / Retirement 401k',
        'Inventory For Sale',
        'Work In Process',
        'Value of Other Businesses Owned',
        'Equipment / Machinery',
    ];
    /** Fixed Step 4 assets that omit the "How Owned?" control (Self / Joint / Trust). */
    public readonly assetHeadersWithoutHowOwned: readonly string[] = [
        'IRA / Pension / Retirement 401k',
        'Inventory For Sale',
        'Work In Process',
        'Value of Other Businesses Owned',
        'Equipment / Machinery',
    ];
    public readonly assetHeaderLists: string[] = [
        'Real Estate Owned or Purchasing',
        'Other Assets',
        'Other Liabilities',
        'Accounts Payable',
        'Vehicles / Trailers / Watercraft / RVs',
        'Accounts / Notes Receivable',
    ]
    public readonly extraAssetHeaders: string[] = [
        "Second Mortgage / HELOC"
    ]

    public readonly MAX_OTHER_ASSETS: number = 2;
    public readonly MAX_OTHER_LIABILITIES: number = 3;
    public readonly MAX_REAL_ESTATE: number = 2;
    public readonly MAX_ACCOUNTS_PAYABLE: number = 1;
    public readonly MAX_VEHICLES: number = 5;

    constructor(
        private bss: BrowserStorageService,
        private s2: StepTwoState,
        private assetsService: AssetsService,
    ) { }

    get state() {
        if (!this._state) {
            const item = this.bss.getSessionItem(StorageKeys.StepFourModel);
            if (item) this._state = item;
            return item ?? undefined;
        }
        return this._state;
    }

    set state(value: StepFourModel | undefined) {
        this.bss.setSessionItem(StorageKeys.StepFourModel, value);
        this._state = value;
    }

    get realEstateName() {
        return this.assetHeaderLists[0];
    }
    get otherAssetsName() {
        return this.assetHeaderLists[1];
    }
    get otherLiabilitiesName() {
        return this.assetHeaderLists[2];
    }
    get accountsPayableName() {
        return this.assetHeaderLists[3];
    }
    get vehiclesName() {
        return this.assetHeaderLists[4];
    }
    get accountsReceivableName() {
        return this.assetHeaderLists[5];
    }

    getListState(name: string) {
        return this.state?.lists[name];
    }
    getExtraState(i: number) {
        const name = this.extraAssetHeaders[i];
        return this.state?.extras[name];
    }

    get helocName() {
        return this.extraAssetHeaders[0];
    }

    private getAssetsTotal() {
        if (!this.state) return 0;
        let total = 0;
        this.assetHeaders.forEach(k => total += this.state?.fixed[k]?.value ?? 0);
        total -= this.state?.fixed['Face Value Life Insurance']?.value ?? 0;
        this.assetHeaderLists.forEach(k => {
            this.state?.lists[k]?.forEach((a) => total += a.value ?? 0);
        })
        return total;
    }

    private getLiabilitiesTotal() {
        if (!this.state) return 0;
        let total = 0;
        this.assetHeaders.forEach(k => total += this.state?.fixed[k]?.amount_owed ?? 0);
        total -= this.state?.fixed['Face Value Life Insurance']?.amount_owed ?? 0;
        this.assetHeaderLists.forEach(k => {
            this.state?.lists[k]?.forEach((a) => total += a.amount_owed ?? 0);
        })
        return total;
    }

    private getMonthlyPayments() {
        if (!this.state) return 0;
        let total = 0;
        this.assetHeaders.forEach(k => total += this.state?.fixed[k]?.monthly_payment ?? 0);
        total -= this.state?.fixed['Face Value Life Insurance']?.monthly_payment ?? 0;
        this.assetHeaderLists.forEach(k => {
            this.state?.lists[k]?.forEach((a) => total += a.monthly_payment ?? 0);
        })
        return total;
    }

    getTotals(): LoanTotal[] {
        const totals: LoanTotal[] = [];

        const assets = this.getAssetsTotal();
        const liabilities = this.getLiabilitiesTotal();
        const monthly = this.getMonthlyPayments();

        totals.push(
            {
                name: 'Total Assets',
                total: assets,
            },
            {
                name: 'Total Liabilities',
                total: liabilities,
            },
            {
                name: 'Net Worth',
                total: assets - liabilities,
            },
            {
                name: 'Monthly Payments',
                total: monthly,
            }
        );

        return totals;
    }

    get isAssetsLiabilitiesEmpty() {
        if (this.state === undefined) return true;
        const empty = !this.assetHeaders.some(k => !isAssetEmpty(this.state!.fixed[k]));
        const empty2 = !this.assetHeaderLists.some(k => this.state!.lists[k].some(a => !isAssetEmpty(a)));
        return empty2 && empty;
    }

    get isRealEstateEmpty() {
        return this._state?.lists[this.realEstateName].some(a => !isAssetEmpty(a)) ?? true;
    }
    get isOtherAssetsEmpty() {
        return this._state?.lists[this.otherAssetsName].some(a => !isAssetEmpty(a)) ?? true;
    }
    get isOtherLiabilitiesEmpty() {
        return this._state?.lists[this.otherLiabilitiesName].some(a => !isAssetEmpty(a)) ?? true;
    }
    get isVehiclesEmpty() {
        return this._state?.lists[this.vehiclesName].some(a => !isAssetEmpty(a)) ?? true;
    }

    delListItem(name: string, idx: number) {
        const asset = this._state?.lists[name].at(idx);
        if (asset) {
            if (asset.uuid) this.assetsService.deleteAsset(asset.uuid).subscribe();
            this._state?.lists[name].splice(idx, 1);
            this.bss.setSessionItem(StorageKeys.StepFourModel, this._state);
        }
    }

    delExtraItem(name: string) {
        const asset = this.state?.extras[name];
        if (asset && asset.uuid) this.assetsService.deleteAsset(asset.uuid).subscribe();
    }

    async syncDB() {
        if (this.state === undefined || !this.s2.state?.applicant.uuid) return;
        const obs: Observable<any>[] = [];
        const applicant_uuid = this.s2.state?.applicant.uuid;

        if (applicant_uuid === undefined) {
            console.error("Error creating asset. Missing Applicant UUID");
            return;
        }

        this.assetHeaders.forEach(k => {
            const asset = this._state!.fixed[k];
            let o: Observable<any> | undefined = undefined;
            if (asset.uuid === undefined && !isAssetEmpty(asset)) {
                o = this.assetsService.createAsset(asset, applicant_uuid).pipe(tap((v) => {
                    asset.uuid = v.uuid;
                }));
            }
            else if (asset.uuid && !isAssetEmpty(asset)) {
                o = this.assetsService.updateAsset(asset.uuid, asset).pipe(tap((v) => {
                    asset.uuid = v.uuid;
                }));
            }
            else if (asset.uuid && isAssetEmpty(asset)) {
                o = this.assetsService.deleteAsset(asset.uuid).pipe(tap(() => {
                    asset.uuid = undefined;
                }));
            }
            if (o) obs.push(o);
        })

        this.assetHeaderLists.forEach(k => {
            const arr = this._state!.lists[k];
            arr.forEach((asset) => {
                let o: Observable<any> | undefined = undefined;
                if (asset.uuid === undefined && !isAssetEmpty(asset)) {
                    o = this.assetsService.createAsset(asset, applicant_uuid).pipe(tap((v) => {
                        asset.uuid = v.uuid;
                    }));
                }
                else if (asset.uuid && !isAssetEmpty(asset)) {
                    o = this.assetsService.updateAsset(asset.uuid, asset).pipe(tap((v) => {
                        asset.uuid = v.uuid;
                    }));
                }
                else if (asset.uuid && isAssetEmpty(asset)) {
                    o = this.assetsService.deleteAsset(asset.uuid).pipe(tap(() => {
                        asset.uuid = undefined;
                    }));
                }
                if (o) obs.push(o);
            })
        })

        this.extraAssetHeaders.forEach(k => {
            const asset = this._state!.extras[k];
            let o: Observable<any> | undefined = undefined;
            if (asset.uuid === undefined && !isAssetEmpty(asset)) {
                o = this.assetsService.createAsset(asset, applicant_uuid).pipe(tap((v) => {
                    asset.uuid = v.uuid;
                }));
            }
            else if (asset.uuid && !isAssetEmpty(asset)) {
                o = this.assetsService.updateAsset(asset.uuid, asset).pipe(tap((v) => {
                    asset.uuid = v.uuid;
                }));
            }
            else if (asset.uuid && isAssetEmpty(asset)) {
                o = this.assetsService.deleteAsset(asset.uuid).pipe(tap(() => {
                    asset.uuid = undefined;
                }));
            }
            if (o) obs.push(o);
        })

        if (obs.length > 0) {
            await lastValueFrom(forkJoin(obs));
        }
        this.bss.setSessionItem(StorageKeys.StepFourModel, this._state);
    }

    async fromDB() {
        if (!this.s2.state || !this.s2.state.applicant.uuid) return;
        const assets = await lastValueFrom(this.assetsService.getAssetsByApplicantId(this.s2.state.applicant.uuid));
        const newState: StepFourModel = { fixed: {}, lists: {}, extras: {} };
        this.assetHeaders.forEach((k) => {
            const asset = assets.find(a => a.name === k);
            if (asset) {
                newState.fixed[k] = asset;
            }
        })
        this.assetHeaderLists.forEach((k) => {
            if (!newState.lists[k]) newState.lists[k] = [];
            const as = assets.filter(a => a.name === k);
            if (as.length > 0) {
                newState.lists[k] = newState.lists[k].concat(as);
            }
        })
        this.extraAssetHeaders.forEach((k) => {
            const asset = assets.find(a => a.name === k);
            if (asset) {
                newState.extras[k] = asset;
            }
        })
        this.state = newState;
    }
}
